import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PRODUCTS } from '../mock/mockData';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { Star, Truck, RotateCcw, ShieldCheck, Minus, Plus, Heart, PlayCircle, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import ProductCarousel from '../components/ProductCarousel';
import { getCatalogProduct, getCatalogProducts } from '../services/catalogService';

const fmt = (n) => '\u20B9 ' + n.toLocaleString('en-IN');
const BASE_SELECTION_ID = 'base';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedVariantId = searchParams.get('variant') || '';
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [product, setProduct] = useState(() => PRODUCTS.find((p) => p.id === id));
  const [catalog, setCatalog] = useState(PRODUCTS);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedOptionId, setSelectedOptionId] = useState(BASE_SELECTION_ID);
  const [fullViewOpen, setFullViewOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setActiveImg(0);
    setSelectedColor('');
    setSelectedSize('');
    setSelectedOptionId(BASE_SELECTION_ID);
    getCatalogProduct(id).then(({ product: loadedProduct }) => {
      if (mounted) setProduct(loadedProduct);
    });
    getCatalogProducts({ limit: 100 }).then(({ products }) => {
      if (mounted) setCatalog(products);
    });
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    const variants = product?.variants || [];
    const requestedVariant = variants.find((variant) =>
      [variant.id, variant._id, variant.sku].filter(Boolean).map(String).includes(String(requestedVariantId))
    );
    if (requestedVariant) {
      setSelectedOptionId(String(requestedVariant.id || requestedVariant._id || requestedVariant.sku));
      setSelectedColor(requestedVariant.colorName || requestedVariant.color || '');
      setSelectedSize(requestedVariant.size || '');
    } else {
      setSelectedOptionId(BASE_SELECTION_ID);
      setSelectedColor(product?.colorName || product?.color || '');
      setSelectedSize(product?.specification || product?.dimensions || product?.material || 'Main product');
    }
    setActiveImg(0);
  }, [product, requestedVariantId]);

  if (!product) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-20 text-center bg-[#FAF7F2]">
        <p className="text-[#1F2A44]">Product not found.</p>
        <Link to="/shop" className="text-[#E8B530] hover:underline mt-3 inline-block">Back to Shop</Link>
      </main>
    );
  }

  const variants = product.variants || [];
  const variantIdOf = (variant, index) => String(variant.id || variant._id || variant.sku || index);
  const selectionOptions = [
    {
      id: BASE_SELECTION_ID,
      type: 'base',
      colorLabel: product.colorName || product.color || 'Main product',
      specLabel: product.specification || product.dimensions || product.material || 'Main product',
      image: product.colorImage || product.image,
      price: product.price,
      stock: product.stock,
      sku: product.sku
    },
    ...variants.map((variant, index) => ({
      id: variantIdOf(variant, index),
      type: 'variant',
      variant,
      colorLabel: variant.colorName || variant.color || 'Variant colour',
      specLabel: variant.size || variant.specification || variant.dimensions || 'Variant specification',
      image: variant.colorImage || variant.images?.[0] || product.image,
      price: variant.price ?? product.price,
      stock: variant.stock ?? product.stock,
      sku: variant.sku || product.sku
    }))
  ];
  const selectedOption = selectionOptions.find((option) => option.id === selectedOptionId) || selectionOptions[0];
  const colorOptions = selectionOptions;
  const specOptions = selectionOptions.filter((option) => option.colorLabel === selectedOption.colorLabel);
  const selectedVariant = selectedOption.type === 'variant' ? selectedOption.variant : null;
  const previewVariant = selectedVariant;
  const requiresSelection = false;
  const canBuy = true;
  const variantImages = previewVariant
    ? (previewVariant.images || []).filter(Boolean)
    : [];
  const effectiveProduct = previewVariant ? {
    ...product,
    price: previewVariant.price ?? product.price,
    marketPrice: previewVariant.marketPrice ?? product.marketPrice,
    stock: previewVariant.stock ?? product.stock,
    sku: previewVariant.sku || product.sku,
    material: previewVariant.material || product.material,
    dimensions: previewVariant.dimensions || product.dimensions,
    specification: previewVariant.specification,
    images: variantImages.length ? [...new Set(variantImages)] : product.images,
    videos: previewVariant.videos?.length ? previewVariant.videos : product.videos,
    image: variantImages[0] || product.image,
    selectedVariantId: selectedVariant?.id || selectedVariant?._id,
    variantLabel: [previewVariant.colorName || previewVariant.color, previewVariant.size].filter(Boolean).join(' / ')
  } : product;
  const images = effectiveProduct.images && effectiveProduct.images.length ? effectiveProduct.images : [effectiveProduct.image];
  const videos = effectiveProduct.videos || [];
  const hasStock = effectiveProduct.stock !== undefined && effectiveProduct.stock !== null;
  const stock = Number(effectiveProduct.stock ?? 0);
  const showLowStock = hasStock && stock < 10;
  const canPurchaseStock = !hasStock || stock > 0;
  const similar = catalog.filter((p) => p.category === product.category && (p.productId || p.id) !== product.id).slice(0, 8);
  const mediaCount = images.length + videos.length;
  const showPrevMedia = () => setActiveImg((current) => (current - 1 + mediaCount) % mediaCount);
  const showNextMedia = () => setActiveImg((current) => (current + 1) % mediaCount);

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-4 text-xs text-[#1F2A44]/70">
        <Link to="/" className="hover:text-[#E8B530]">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-[#E8B530]">Shop</Link>
        <span className="mx-2">/</span>
        <span className="text-[#1F2A44]">{product.name}</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10 py-6">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square bg-[#F3EFE8] border border-[#1F2A44]/10 overflow-hidden">
            {activeImg >= images.length && videos[activeImg - images.length] ? (
              <video src={videos[activeImg - images.length].url} controls className="w-full h-full object-cover" />
            ) : (
              <button type="button" onClick={() => setFullViewOpen(true)} className="block h-full w-full">
                <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setFullViewOpen(true)}
              className="absolute right-3 top-3 inline-flex items-center gap-2 bg-white/90 px-3 py-2 text-xs font-semibold text-[#1F2A44] shadow hover:bg-white"
            >
              <Maximize2 size={15} />
              View all
            </button>
          </div>
          {(images.length + videos.length) > 1 && (
            <div className="relative mt-3">
              <div className="flex gap-3 overflow-x-auto no-scrollbar py-1">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 border ${i === activeImg ? 'border-[#E8B530]' : 'border-[#1F2A44]/15'} overflow-hidden hover:border-[#E8B530] transition-colors flex-shrink-0`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                {videos.map((video, i) => (
                  <button key={video.url} onClick={() => setActiveImg(images.length + i)} className={`relative w-20 h-20 border ${images.length + i === activeImg ? 'border-[#E8B530]' : 'border-[#1F2A44]/15'} overflow-hidden hover:border-[#E8B530] transition-colors bg-[#1F2A44] text-white text-xs flex-shrink-0`}>
                    <video src={video.url} muted className="h-full w-full object-cover opacity-70" />
                    <PlayCircle size={22} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </button>
                ))}
              </div>

              
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#1F2A44] leading-tight">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3 text-sm">
            <div className="flex items-center gap-1 bg-[#E8B530]/15 text-[#1F2A44] px-2 py-0.5 rounded-sm">
              <Star size={12} className="fill-[#E8B530] text-[#E8B530]" />
              <span className="font-semibold">{product.rating}</span>
            </div>
            <span className="text-[#1F2A44]/60">({product.reviews} reviews)</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl font-bold text-[#1F2A44]">{fmt(effectiveProduct.price)}</span>
            {effectiveProduct.marketPrice > effectiveProduct.price && (
              <>
                <span className="text-[#1F2A44]/50 line-through">{fmt(effectiveProduct.marketPrice)}</span>
                <span className="text-green-700 font-semibold text-sm">({Math.round(((effectiveProduct.marketPrice - effectiveProduct.price) / effectiveProduct.marketPrice) * 100)}% off)</span>
              </>
            )}
          </div>
          <p className="text-xs text-[#1F2A44]/60 mt-1">Inclusive of all taxes. Free shipping over ₹10,000.</p>
          {showLowStock && (
            <p className="mt-3 inline-flex bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
              {stock > 0 ? `Hurry up, only ${stock} left. Book or buy now.` : 'This option is currently out of stock.'}
            </p>
          )}

          <p className="text-[#1F2A44]/80 mt-5 leading-relaxed text-sm">{product.description}</p>

          {selectionOptions.length > 0 && (
            <div className="mt-5 space-y-4">
              {colorOptions.length > 0 && (
                <div>
                  <p className="text-xs uppercase text-[#1F2A44]/60 mb-2">Colour</p>
                  <div className="flex flex-wrap gap-2">
                    {colorOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setSelectedOptionId(option.id);
                          setSelectedColor(option.colorLabel);
                          setSelectedSize(option.specLabel);
                          setActiveImg(0);
                        }}
                        className={`flex min-w-[120px] items-center gap-2 border p-2 text-left text-sm transition-colors ${selectedOptionId === option.id ? 'border-[#E8B530] bg-[#E8B530]/15' : 'border-[#1F2A44]/15 bg-white hover:border-[#E8B530]'}`}
                      >
                        <span className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-sm bg-[#F3EFE8]">
                          {option.image ? <img src={option.image} alt={option.colorLabel} className="h-full w-full object-cover" /> : null}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-medium text-[#1F2A44]">{option.colorLabel}</span>
                          <span className="block text-[11px] text-[#1F2A44]/50">{option.type === 'base' ? 'Main product' : 'Variant'}</span>
                          <span className="block text-xs text-[#1F2A44]/60">From {fmt(option.price)}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {specOptions.length > 0 && (
                <div>
                  <p className="text-xs uppercase text-[#1F2A44]/60 mb-2">Specification</p>
                  <div className="flex flex-wrap gap-2">
                    {specOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setSelectedOptionId(option.id);
                          setSelectedColor(option.colorLabel);
                          setSelectedSize(option.specLabel);
                          setActiveImg(0);
                        }}
                        className={`px-4 py-2 border text-sm ${selectedOptionId === option.id ? 'border-[#E8B530] bg-[#E8B530]/15' : 'border-[#1F2A44]/15 bg-white hover:border-[#E8B530]'}`}
                      >
                        {option.specLabel}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-sm text-[#1F2A44]/70">Stock: {effectiveProduct.stock ?? 0} | SKU: {effectiveProduct.sku}</p>
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="border border-[#1F2A44]/10 p-3 bg-white">
              <p className="text-xs text-[#1F2A44]/60 uppercase">Material</p>
              <p className="text-[#1F2A44] font-medium">{effectiveProduct.material}</p>
            </div>
            <div className="border border-[#1F2A44]/10 p-3 bg-white">
              <p className="text-xs text-[#1F2A44]/60 uppercase">Dimensions</p>
              <p className="text-[#1F2A44] font-medium">{effectiveProduct.dimensions}</p>
            </div>
            <div className="border border-[#1F2A44]/10 p-3 bg-white col-span-2">
              <p className="text-xs text-[#1F2A44]/60 uppercase">Warranty</p>
              <p className="text-[#1F2A44] font-medium">{product.warranty}</p>
            </div>
            {effectiveProduct.specification && (
              <div className="border border-[#1F2A44]/10 p-3 bg-white col-span-2">
                <p className="text-xs text-[#1F2A44]/60 uppercase">Specification</p>
                <p className="text-[#1F2A44] font-medium">{effectiveProduct.specification}</p>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-[#1F2A44]/20 bg-white">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:bg-[#F3EFE8] transition-colors text-[#1F2A44]"><Minus size={14} /></button>
              <span className="w-10 text-center text-[#1F2A44] font-semibold">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:bg-[#F3EFE8] transition-colors text-[#1F2A44]"><Plus size={14} /></button>
            </div>
            <button className="w-11 h-11 border border-[#1F2A44]/20 bg-white hover:bg-[#E8B530] hover:border-[#E8B530] flex items-center justify-center text-[#1F2A44] transition-colors" aria-label="wishlist">
              <Heart size={18} />
            </button>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button
              disabled={!canBuy || !canPurchaseStock}
              onClick={() => { addToCart(effectiveProduct, qty); toast({ title: 'Added to cart', description: effectiveProduct.name }); }}
              className="flex-1 bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] font-semibold py-3.5 uppercase tracking-wider text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >Add to Cart</button>
            <button
              disabled={!canBuy || !canPurchaseStock}
              onClick={() => { addToCart(effectiveProduct, qty); navigate('/cart'); }}
              className="flex-1 bg-[#E8B530] hover:bg-[#D4A220] text-[#1F2A44] font-semibold py-3.5 uppercase tracking-wider text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >Buy Now</button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-xs text-[#1F2A44]">
            <div className="flex flex-col items-center text-center p-3 border border-[#1F2A44]/10"><Truck size={18} className="text-[#E8B530] mb-1" /> Free Shipping</div>
            <div className="flex flex-col items-center text-center p-3 border border-[#1F2A44]/10"><RotateCcw size={18} className="text-[#E8B530] mb-1" /> 7-Day Returns</div>
            <div className="flex flex-col items-center text-center p-3 border border-[#1F2A44]/10"><ShieldCheck size={18} className="text-[#E8B530] mb-1" /> Warranty</div>
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <div className="border-t border-[#1F2A44]/10 bg-[#FAF7F2]">
          <ProductCarousel title="Related products" products={similar} alwaysShowButtons={true} />
        </div>
      )}

      {fullViewOpen && (
        <div className="fixed inset-0 z-[100] bg-white text-[#1F2A44]">
          <button
            type="button"
            onClick={() => setFullViewOpen(false)}
            className="absolute right-5 top-5 z-10 rounded-full bg-[#1F2A44]/10 p-3 hover:bg-[#1F2A44]/15"
            aria-label="Close image viewer"
          >
            <X size={24} />
          </button>
          {mediaCount > 1 && (
            <>
              <button
                type="button"
                onClick={showPrevMedia}
                className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-[#1F2A44]/10 p-3 hover:bg-[#1F2A44]/15"
                aria-label="Previous image"
              >
                <ChevronLeft size={30} />
              </button>
              <button
                type="button"
                onClick={showNextMedia}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-[#1F2A44]/10 p-3 hover:bg-[#1F2A44]/15"
                aria-label="Next image"
              >
                <ChevronRight size={30} />
              </button>
            </>
          )}
          <div className="flex h-full w-full items-center justify-center p-6 sm:p-10">
            {activeImg >= images.length && videos[activeImg - images.length] ? (
              <video src={videos[activeImg - images.length].url} controls autoPlay className="max-h-full max-w-full object-contain" />
            ) : (
              <img src={images[activeImg]} alt={product.name} className="max-h-full max-w-full object-contain" />
            )}
          </div>
          {mediaCount > 1 && (
            <div className="absolute bottom-5 left-1/2 flex max-w-[90vw] -translate-x-1/2 gap-2 overflow-x-auto rounded-full border border-[#1F2A44]/10 bg-white/95 px-4 py-3 shadow-lg">
              {images.map((image, index) => (
                <button key={image || index} type="button" onClick={() => setActiveImg(index)} className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded border ${activeImg === index ? 'border-[#E8B530]' : 'border-[#1F2A44]/20'}`}>
                  <img src={image} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
              {videos.map((video, index) => (
                <button key={video.url} type="button" onClick={() => setActiveImg(images.length + index)} className={`relative h-14 w-14 flex-shrink-0 overflow-hidden rounded border ${activeImg === images.length + index ? 'border-[#E8B530]' : 'border-[#1F2A44]/20'}`}>
                  <video src={video.url} muted className="h-full w-full object-cover opacity-80" />
                  <PlayCircle size={18} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default ProductDetailPage;
